import {
  Veterinaria, Optica, FarmaciaClinica, Consultorios, Pacientes, Notas, Signos, Citas,
  Laboratorio, Imagenologia, Cuentas, Cobros, type Paciente, type Signo,
} from './api';
import {
  CATALOGO_MEDICAMENTOS_MSPAS, MEDICAMENTOS_ALTA_ROTACION, getMedicamentoByCodigo, medicamentoToProductoSeed,
} from './farmacia-mspas-catalog';
import { seedPlacasDemo, type PlacaDental } from './expediente-rx';
type ClinicPreviewOrg =
  | 'clinica-medica'
  | 'clinica-odontologica'
  | 'clinica-veterinaria'
  | 'clinica-nutricion'
  | 'clinica-psicologia'
  | 'clinica-estetica'
  | 'clinica-optica'
  | 'clinica-fisioterapia';
import { FISIO_PROFESIONAL_DEMO } from './fisioterapia';
import type { RutinaFisio } from './fisioterapia-rutina';
import {
  RUTINA_FISIO_TIPO,
  serializeRutina,
} from './fisioterapia-rutina';

const RUTINA_DEMO_LUMBAR: RutinaFisio = {
  titulo: 'Rehabilitación lumbar — plan domiciliar',
  indicaciones: 'Realizar 2 veces al día. Crioterapia 15 min post sesión si EVA > 4. Suspender si aumenta el dolor.',
  ejercicios: [
    { nombre: 'Puente glúteo', series: 3, reps: 10, hold: '2 s', frecuencia: '2×/día', progresion: 'Subir a 3×12 si EVA < 4' },
    { nombre: 'Estiramiento isquiotibiales', series: 3, reps: 1, hold: '30 s', frecuencia: '2×/día', progresion: 'Mantener sin dolor > 6/10 EVA' },
    { nombre: 'Marcha activa', series: 1, reps: 1, hold: '10 min', frecuencia: '1×/día', progresion: 'Aumentar 2 min por semana' },
  ],
};

const RUTINA_DEMO_HOMBRO: RutinaFisio = {
  titulo: 'Manguito rotador — rutina domiciliaria',
  indicaciones: 'Evitar cargar peso > 2 kg con brazo afectado. Aplicar hielo 10 min si hay inflamación post ejercicio.',
  ejercicios: [
    { nombre: 'Pendulares Codman', series: 3, reps: 15, hold: '—', frecuencia: '3×/día', progresion: 'Aumentar amplitud gradual' },
    { nombre: 'Rotación externa con banda', series: 3, reps: 12, hold: '—', frecuencia: '1×/día', progresion: 'Banda amarilla → roja' },
    { nombre: 'Estiramiento posterior hombro', series: 3, reps: 1, hold: '30 s', frecuencia: '2×/día', progresion: '—' },
  ],
};
import { type SeedResult, hoy, horaDemo, tryStep } from './demo-seed-shared';
import { seedHospitalDemo } from './hospital-demo-seed';

export type { SeedResult } from './demo-seed-shared';

/** CUIs demo de 13 dígitos para expediente clínico (humanos). */
export const DEMO_CUIS = {
  medico: '1234567890101',
  dental: '2345678901212',
  nutricion: '3456789012323',
  psicologia: '4567890123434',
  optica: '5678901234545',
  estetica: '6789012345656',
  fisioterapia: '2233445566778',
  secundario: '7890123456767',
} as const;

export const EXPEDIENTE_CUI_STORAGE = 'toscana.expediente.cui';

type DemoNota = { autor: string; contenido: string };

type DemoPatientDef = {
  cui: string;
  nombres: string;
  apellidos: string;
  sexo: 'M' | 'F';
  telefono?: string;
  fechaNacimiento?: string;
  notas: DemoNota[];
};

type CitaDemoDef = {
  hour: number;
  min?: number;
  motivo: string;
  profesional: string;
  consultorio: string;
  estado?: 'PROGRAMADA' | 'CONFIRMADA' | 'ATENDIDA';
  pacienteIdx?: number;
};

const RUBRO_PACIENTES: Partial<Record<ClinicPreviewOrg, DemoPatientDef[]>> = {
  'clinica-medica': [
    {
      cui: DEMO_CUIS.medico, nombres: 'Pedro', apellidos: 'García López', sexo: 'M',
      telefono: '5025550505', fechaNacimiento: '1985-03-15',
      notas: [
        { autor: 'Dr. Demo', contenido: 'Control ambulatorio. HTA controlada. Continuar losartán 50 mg c/24h.' },
        { autor: 'Dr. Demo', contenido: 'Laboratorios: glucosa 98 mg/dL, creatinina normal. Plan: control en 3 meses.' },
        { autor: 'Enfermería Demo', contenido: 'Signos vitales estables al ingreso. Sin alergias medicamentosas conocidas.' },
      ],
    },
    {
      cui: DEMO_CUIS.secundario, nombres: 'Sofía', apellidos: 'Martínez Ruiz', sexo: 'F',
      telefono: '5025550606', fechaNacimiento: '1992-07-22',
      notas: [
        { autor: 'Dr. Demo', contenido: 'Primera consulta. Cuadro respiratorio leve. Paracetamol 500 mg PRN.' },
        { autor: 'Dr. Demo', contenido: 'Evolución favorable a 48 h. Tos seca residual. Hidratación oral.' },
      ],
    },
  ],
  'clinica-odontologica': [
    {
      cui: DEMO_CUIS.dental, nombres: 'Laura', apellidos: 'Hernández', sexo: 'F',
      telefono: '5025550707', fechaNacimiento: '1990-11-08',
      notas: [
        { autor: 'Dra. Odonto Demo', contenido: 'Evaluación inicial. Caries en 16 y 26. Periapicales indicadas.' },
        { autor: 'Dra. Odonto Demo', contenido: 'Endodoncia pieza 16 iniciada. Anestesia local sin complicaciones.' },
        { autor: 'Dra. Odonto Demo', contenido: 'Panorámica archivada. Plan: restauración 26 en próxima cita.' },
      ],
    },
    {
      cui: '1122334455667', nombres: 'Diego', apellidos: 'Morales', sexo: 'M',
      telefono: '5025550808', fechaNacimiento: '1988-04-03',
      notas: [
        { autor: 'Dra. Odonto Demo', contenido: 'Profilaxis y fluorización. Control en 6 meses.' },
      ],
    },
  ],
  'clinica-nutricion': [
    {
      cui: DEMO_CUIS.nutricion, nombres: 'Carla', apellidos: 'Mejía', sexo: 'F',
      telefono: '5025550909', fechaNacimiento: '1987-01-14',
      notas: [
        { autor: 'Lic. Nutrición Demo', contenido: 'Plan hipocalórico 1600 kcal. Meta: -4 kg en 8 semanas.' },
        { autor: 'Lic. Nutrición Demo', contenido: 'Recordatorio de alimentación: 3 comidas + 2 colaciones. Agua 2 L/día.' },
        { autor: 'Lic. Nutrición Demo', contenido: 'Seguimiento semana 2: -1.2 kg. Adherencia buena al plan.' },
      ],
    },
    {
      cui: '8901234567878', nombres: 'Roberto', apellidos: 'Castillo', sexo: 'M',
      telefono: '5025551010', fechaNacimiento: '1975-09-30',
      notas: [
        { autor: 'Lic. Nutrición Demo', contenido: 'DM2. Educación en carbohidratos. Registro de glucemias en ayunas.' },
      ],
    },
  ],
  'clinica-psicologia': [
    {
      cui: DEMO_CUIS.psicologia, nombres: 'Elena', apellidos: 'Vásquez', sexo: 'F',
      telefono: '5025551111', fechaNacimiento: '1995-06-18',
      notas: [
        { autor: 'Lic. Psico Demo', contenido: 'Sesión 1/12 — Evaluación inicial. Ansiedad generalizada leve-moderada.' },
        { autor: 'Lic. Psico Demo', contenido: 'Sesión 3/12 TCC. Reestructuración de pensamientos automáticos. Tarea: registro diario.' },
        { autor: 'Lic. Psico Demo', contenido: 'Sesión 4/12. Mejoría subjetiva del sueño. Continuar técnicas de respiración.' },
      ],
    },
    {
      cui: '9012345678989', nombres: 'Miguel', apellidos: 'Torres', sexo: 'M',
      telefono: '5025551212', fechaNacimiento: '1982-12-05',
      notas: [
        { autor: 'Lic. Psico Demo', contenido: 'Evaluación inicial. Estrés laboral. Psicoeducación en límites personales.' },
      ],
    },
  ],
  'clinica-estetica': [
    {
      cui: DEMO_CUIS.estetica, nombres: 'Valeria', apellidos: 'Pineda', sexo: 'F',
      telefono: '5025551313', fechaNacimiento: '1991-03-25',
      notas: [
        { autor: 'Dra. Estética Demo', contenido: 'Toxina botulínica región frontal 20 U. Consentimiento informado firmado.' },
        { autor: 'Dra. Estética Demo', contenido: 'Control 7 días: simetría adecuada. Sin ptosis palpebral.' },
        { autor: 'Enfermería Demo', contenido: 'Fotografía clínica pre/post archivada en expediente.' },
      ],
    },
    {
      cui: '0123456789090', nombres: 'Andrea', apellidos: 'Solís', sexo: 'F',
      telefono: '5025551414', fechaNacimiento: '1989-08-11',
      notas: [
        { autor: 'Dra. Estética Demo', contenido: 'Ácido hialurónico labios 1 ml. Control post 48 h sin hematomas.' },
      ],
    },
  ],
  'clinica-optica': [
    {
      cui: DEMO_CUIS.optica, nombres: 'Ana', apellidos: 'Ruiz', sexo: 'F',
      telefono: '5025550303', fechaNacimiento: '1988-02-20',
      notas: [
        { autor: 'Lic. Opto Demo', contenido: 'Examen optométrico anual. Miopía leve OD -1.25 / OI -1.00. Uso permanente de lentes.' },
        { autor: 'Lic. Opto Demo', contenido: 'Orden de taller generada. Armazón Ray-Ban + mica orgánica 1.56.' },
      ],
    },
    {
      cui: '9988776655443', nombres: 'Luis', apellidos: 'Ortega', sexo: 'M',
      telefono: '5025550404', fechaNacimiento: '1979-10-12',
      notas: [
        { autor: 'Lic. Opto Demo', contenido: 'Presbicia incipiente. Add +1.00. Lentes progresivos indicados.' },
      ],
    },
  ],
  'clinica-fisioterapia': [
    {
      cui: DEMO_CUIS.fisioterapia, nombres: 'Jorge', apellidos: 'Ramírez', sexo: 'M',
      telefono: '5025551515', fechaNacimiento: '1983-05-09',
      notas: [
        { autor: FISIO_PROFESIONAL_DEMO, contenido: 'Evaluación inicial — lumbalgia post-quirúrgica (M54.5). EVA 7/10 · flexión lumbar 40°. Objetivo: reducir dolor y recuperar marcha.' },
        { autor: FISIO_PROFESIONAL_DEMO, contenido: 'Plan 12 sesiones (3×/sem). Modalidades: TENS lumbar 15 min, ultrasonido 1 MHz, movilización segmentaria. Rutina domiciliaria: puente glúteo 3×10, estiramiento isquios 3×30 s, crioterapia post sesión 15 min.' },
        { autor: FISIO_PROFESIONAL_DEMO, contenido: 'Sesión 4/12 · Subjetivo: EVA 4/10. Objetivo: flexión 55°. Aplicado masaje descontracturante + ejercicio en plataforma. Paciente tolera progresión.' },
      ],
    },
    {
      cui: '3344556677889', nombres: 'Patricia', apellidos: 'Morales', sexo: 'F',
      telefono: '5025551616', fechaNacimiento: '1990-12-18',
      notas: [
        { autor: FISIO_PROFESIONAL_DEMO, contenido: 'Hombro derecho — tendinitis manguito rotador (M75.1). EVA 5/10 · abducción activa 90° · MMT supraespinoso 3/5.' },
        { autor: FISIO_PROFESIONAL_DEMO, contenido: 'Sesión 2/8 · Pendulares Codman, bandas elásticas rotación externa 3×12, kinesiotape estabilizador. Tarea: estiramiento posterior 3×30 s/día.' },
      ],
    },
  ],
};

/** CUI principal para abrir expediente clínico en demo por rubro. */
export function demoCuiPrincipal(org: ClinicPreviewOrg | string | null | undefined): string {
  switch (org) {
    case 'clinica-odontologica': return DEMO_CUIS.dental;
    case 'clinica-nutricion': return DEMO_CUIS.nutricion;
    case 'clinica-psicologia': return DEMO_CUIS.psicologia;
    case 'clinica-estetica': return DEMO_CUIS.estetica;
    case 'clinica-optica': return DEMO_CUIS.optica;
    case 'clinica-fisioterapia': return DEMO_CUIS.fisioterapia;
    default: return DEMO_CUIS.medico;
  }
}

export function demoExpedienteHints(
  org: ClinicPreviewOrg | string | null | undefined,
): { cui: string; nombre: string } {
  const defs = RUBRO_PACIENTES[org as ClinicPreviewOrg] ?? RUBRO_PACIENTES['clinica-medica']!;
  const d = defs[0];
  return { cui: d.cui, nombre: `${d.nombres} ${d.apellidos}` };
}

/** Fija el CUI que ExpedienteClinicoView abre automáticamente tras sembrar. */
export function pinExpedienteDemoCui(org: ClinicPreviewOrg | string | null | undefined): string {
  const cui = demoCuiPrincipal(org);
  try { sessionStorage.setItem(EXPEDIENTE_CUI_STORAGE, cui); } catch { /* ignore */ }
  return cui;
}

const CITAS_RUBRO: Partial<Record<ClinicPreviewOrg, CitaDemoDef[]>> = {
  'clinica-medica': [
    { hour: 8, min: 30, motivo: 'Control HTA', profesional: 'Dr. Demo', consultorio: 'Consultorio 1', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 10, min: 0, motivo: 'Primera vez', profesional: 'Dr. Demo', consultorio: 'Consultorio 2', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 11, min: 30, motivo: 'Resultados lab', profesional: 'Dr. Demo', consultorio: 'Consultorio 1', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
  'clinica-odontologica': [
    { hour: 9, min: 0, motivo: 'Evaluación dental', profesional: 'Dra. Odonto Demo', consultorio: 'Sillón 1', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 10, min: 30, motivo: 'Profilaxis', profesional: 'Dra. Odonto Demo', consultorio: 'Sillón 2', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 15, min: 0, motivo: 'Endodoncia 16', profesional: 'Dra. Odonto Demo', consultorio: 'Sillón 1', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
  'clinica-nutricion': [
    { hour: 8, min: 0, motivo: 'Control peso', profesional: 'Lic. Nutrición Demo', consultorio: 'Box A', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 9, min: 30, motivo: 'Plan DM2', profesional: 'Lic. Nutrición Demo', consultorio: 'Box B', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 11, min: 0, motivo: 'Seguimiento mensual', profesional: 'Lic. Nutrición Demo', consultorio: 'Box A', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
  'clinica-psicologia': [
    { hour: 9, min: 0, motivo: 'Sesión TCC', profesional: 'Lic. Psico Demo', consultorio: 'Sala 1', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 10, min: 30, motivo: 'Evaluación inicial', profesional: 'Lic. Psico Demo', consultorio: 'Sala 2', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 14, min: 0, motivo: 'Sesión de seguimiento', profesional: 'Lic. Psico Demo', consultorio: 'Sala 1', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
  'clinica-estetica': [
    { hour: 10, min: 0, motivo: 'Botox frontal', profesional: 'Dra. Estética Demo', consultorio: 'Cabina 1', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 11, min: 30, motivo: 'Rellenos labiales', profesional: 'Dra. Estética Demo', consultorio: 'Cabina 2', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 16, min: 0, motivo: 'Control post', profesional: 'Dra. Estética Demo', consultorio: 'Cabina 1', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
  'clinica-fisioterapia': [
    { hour: 8, min: 0, motivo: 'Sesión rehabilitación lumbar', profesional: FISIO_PROFESIONAL_DEMO, consultorio: 'Box Rehab 1', estado: 'CONFIRMADA', pacienteIdx: 0 },
    { hour: 9, min: 30, motivo: 'Evaluación inicial hombro', profesional: FISIO_PROFESIONAL_DEMO, consultorio: 'Box Rehab 2', estado: 'PROGRAMADA', pacienteIdx: 1 },
    { hour: 11, min: 0, motivo: 'Control sesión 5/12', profesional: FISIO_PROFESIONAL_DEMO, consultorio: 'Box Rehab 1', estado: 'PROGRAMADA', pacienteIdx: 0 },
  ],
};

const CITAS_VET: CitaDemoDef[] = [
  { hour: 8, min: 30, motivo: 'Vacuna anual', profesional: 'Dr. Vet Demo', consultorio: 'Consulta 1', estado: 'CONFIRMADA' },
  { hour: 9, min: 15, motivo: 'Control peso', profesional: 'Dr. Vet Demo', consultorio: 'Consulta 1', estado: 'PROGRAMADA' },
  { hour: 10, min: 0, motivo: 'Desparasitación', profesional: 'Dr. Vet Demo', consultorio: 'Consulta 2', estado: 'PROGRAMADA' },
  { hour: 11, min: 30, motivo: 'Revisión post-internamiento', profesional: 'Dr. Vet Demo', consultorio: 'Consulta 2', estado: 'PROGRAMADA' },
];

const CITAS_OPTICA: CitaDemoDef[] = [
  { hour: 9, min: 0, motivo: 'Examen optométrico', profesional: 'Lic. Opto Demo', consultorio: 'Cabina 1', estado: 'CONFIRMADA' },
  { hour: 10, min: 30, motivo: 'Entrega de lentes', profesional: 'Lic. Opto Demo', consultorio: 'Mostrador', estado: 'PROGRAMADA' },
  { hour: 14, min: 0, motivo: 'Control anual', profesional: 'Lic. Opto Demo', consultorio: 'Cabina 2', estado: 'PROGRAMADA' },
];

const NOMBRES_CITAS_VET = ['Rocky (Labrador)', 'Misha (Siamés)', 'Toby (Beagle)', 'Luna (Pastor)'];
const NOMBRES_CITAS_OPTICA = ['Ana Ruiz', 'Luis Ortega', 'Carla Mejía'];

// ── Veterinaria ──────────────────────────────────────────────────────────────

export async function seedVeterinariaDemo(): Promise<SeedResult> {
  const details: string[] = [];
  try {
    let mascotas = await Veterinaria.mascotas().catch(() => []);

    if (mascotas.length < 3) {
      const p1 = await Veterinaria.crearPropietario({ nombre: 'María López', telefono: '5025550101', email: 'maria@demo.gt' });
      const p2 = await Veterinaria.crearPropietario({ nombre: 'Carlos Méndez', telefono: '5025550202' });
      details.push('2 dueños');

      const m1 = await Veterinaria.crearMascota({
        propietarioId: p1.id!, nombre: 'Rocky', especie: 'PERRO', raza: 'Labrador', sexo: 'MACHO',
      });
      const m2 = await Veterinaria.crearMascota({
        propietarioId: p1.id!, nombre: 'Misha', especie: 'GATO', raza: 'Siamés', sexo: 'HEMBRA',
      });
      const m3 = await Veterinaria.crearMascota({
        propietarioId: p2.id!, nombre: 'Toby', especie: 'PERRO', raza: 'Beagle', sexo: 'MACHO',
      });
      mascotas = [m1, m2, m3];
      details.push('3 mascotas');
    } else {
      details.push(`${mascotas.length} mascotas`);
    }

    const rocky = mascotas.find(m => m.nombre === 'Rocky') ?? mascotas[0];
    const misha = mascotas.find(m => m.nombre === 'Misha') ?? mascotas[1];
    const toby = mascotas.find(m => m.nombre === 'Toby') ?? mascotas[2];

    if (rocky?.id) {
      await Veterinaria.agregarVacuna(rocky.id, {
        nombre: 'Antirrábica', fechaAplicacion: hoy(), proximaDosis: null, lote: 'AR-2026', veterinario: 'Dr. Vet Demo',
      }).catch(() => {});
      await Veterinaria.registrarPeso(rocky.id, { fecha: hoy(), pesoKg: 28.5, nota: 'Control rutina' }).catch(() => {});
      await Veterinaria.agregarNotaExpediente(rocky.id, {
        autor: 'Dr. Vet Demo',
        contenido: 'Control anual. Afebril, mucosas rosadas. Continuar alimento adulto.',
        tipo: 'EVOLUCION',
      }).catch(() => {});
    }
    if (misha?.id) {
      await Veterinaria.agregarNotaExpediente(misha.id, {
        autor: 'Dr. Vet Demo',
        contenido: 'Consulta por vómito leve. Hidratación oral y dieta blanda 48 h.',
        tipo: 'ANAMNESIS',
      }).catch(() => {});
    }
    details.push('Expediente mascota (Rocky, Misha)');

    const jaulas = await Veterinaria.jaulas().catch(() => []);
    if (jaulas.length < 3) {
      await Veterinaria.crearJaula({ numero: 'J-01', area: 'Observación', tamano: 'MEDIANA', estado: 'DISPONIBLE' }).catch(() => {});
      await Veterinaria.crearJaula({ numero: 'J-02', area: 'Post-op', tamano: 'GRANDE', estado: 'DISPONIBLE' }).catch(() => {});
      await Veterinaria.crearJaula({ numero: 'J-03', area: 'Infecciosos', tamano: 'PEQUENA', estado: 'OCUPADA' }).catch(() => {});
      details.push('3 jaulas');
    }

    const prods = await Veterinaria.productos().catch(() => []);
    if (prods.length < 3) {
      await Veterinaria.crearProducto({
        codigo: 'ALM-PUPPY-15', nombre: 'Alimento cachorro 15 kg', categoria: 'ALIMENTO',
        precio: 285, stock: 24, unidad: 'SACO', soloPacientes: false,
      }).catch(() => {});
      await Veterinaria.crearProducto({
        codigo: 'ALM-ADULT-20', nombre: 'Alimento adulto 20 kg', categoria: 'ALIMENTO',
        precio: 320, stock: 18, unidad: 'SACO', soloPacientes: false,
      }).catch(() => {});
      await Veterinaria.crearProducto({
        codigo: 'DESP-PIP', nombre: 'Desparasitante oral', categoria: 'MEDICAMENTO',
        precio: 45, stock: 40, unidad: 'UNIDAD', soloPacientes: true,
      }).catch(() => {});
      details.push('Catálogo mostrador vet');
    }

    const jaulas2 = await Veterinaria.jaulas().catch(() => []);
    const libre = jaulas2.find(j => j.estado === 'DISPONIBLE');
    if (libre && toby?.id) {
      const ints = await Veterinaria.internamientosActivos().catch(() => []);
      if (ints.length === 0) {
        await Veterinaria.internar({
          mascotaId: toby.id, jaulaId: libre.id!, motivo: 'Observación post-vacuna', veterinario: 'Dr. Vet Demo',
        }).catch(() => {});
        details.push('1 internamiento activo');
      }
    }

    await seedCitasGeneric(CITAS_VET, NOMBRES_CITAS_VET, details, 'Citas vet hoy');

    return { ok: true, message: 'Demo veterinaria sembrada.', details };
  } catch (e: any) {
    return { ok: false, message: e?.message ?? String(e), details };
  }
}

// ── Óptica ───────────────────────────────────────────────────────────────────

export async function seedOpticaDemo(): Promise<SeedResult> {
  const details: string[] = [];
  try {
    let pacs = await Optica.pacientes().catch(() => []);

    if (pacs.length < 2) {
      try {
        await Pacientes.crear({
          nombres: 'Ana', apellidos: 'Ruiz', cui: DEMO_CUIS.optica, sexo: 'F', telefono: '5025550303',
        });
        details.push(`Expediente CUI ${DEMO_CUIS.optica}`);
      } catch { /* ya existe */ }

      const pa = await Optica.crearPaciente({
        nombre: 'Ana Ruiz', telefono: '5025550303', sexo: 'FEMENINO', ocupacion: 'Contadora', documento: DEMO_CUIS.optica,
      }).catch(async () => pacs[0]);
      const pb = await Optica.crearPaciente({ nombre: 'Luis Ortega', telefono: '5025550404', sexo: 'MASCULINO' }).catch(async () => pacs[1]);
      pacs = [pa, pb].filter(Boolean);
      details.push('2 pacientes óptica');
    } else {
      details.push(`${pacs.length} pacientes óptica`);
    }

    const ana = pacs.find(p => p.nombre?.includes('Ana')) ?? pacs[0];
    const luis = pacs.find(p => p.nombre?.includes('Luis')) ?? pacs[1];

    const examenes = ana?.id ? await Optica.examenesDe(ana.id).catch(() => []) : [];
    if (examenes.length < 1 && ana?.id) {
      await Optica.crearExamen(ana.id, {
        fecha: hoy(), optometrista: 'Lic. Opto Demo', motivo: 'Control anual', usaLentes: true,
        odEsfera: -1.25, odCilindro: -0.50, odEje: 180, odAdd: 0, odAv: '20/20',
        oiEsfera: -1.00, oiCilindro: -0.25, oiEje: 175, oiAdd: 0, oiAv: '20/25',
        tipoLente: 'MONOFOCAL', diagnostico: 'Miopía leve', recomendaciones: 'Uso permanente',
      }).catch(() => {});
      details.push('1 examen optométrico');
    }

    const arts = await Optica.articulos().catch(() => []);
    if (arts.length < 3) {
      await Optica.crearArticulo({
        codigo: 'ARM-RAY-001', nombre: 'Armazón Ray-Ban clásico', categoria: 'ARMAZON',
        marca: 'Ray-Ban', precio: 850, costo: 420, stock: 6, stockMinimo: 2,
      }).catch(() => {});
      await Optica.crearArticulo({
        codigo: 'MICA-ORG-156', nombre: 'Mica orgánica 1.56', categoria: 'MICA',
        precio: 320, costo: 140, stock: 20, stockMinimo: 5,
      }).catch(() => {});
      await Optica.crearArticulo({
        codigo: 'ARM-OAK-002', nombre: 'Armazón Oakley sport', categoria: 'ARMAZON',
        marca: 'Oakley', precio: 720, costo: 380, stock: 4, stockMinimo: 1,
      }).catch(() => {});
      details.push('Inventario óptica');
    }

    const ords = await Optica.ordenes().catch(() => []);
    if (ords.length < 2 && ana?.id) {
      await Optica.crearOrden({
        pacienteId: ana.id, estado: 'EN_LABORATORIO', fechaPromesa: hoy(),
        armazonMarca: 'Ray-Ban', micaMaterial: 'ORGANICO', micaDiseno: 'MONOFOCAL',
        gOdEsfera: -1.25, gOdCilindro: -0.5, gOdEje: 180,
        gOiEsfera: -1.0, gOiCilindro: -0.25, gOiEje: 175,
        costoArmazon: 850, costoMicas: 320, total: 1170, anticipo: 500,
      }).catch(() => {});
      if (luis?.id) {
        await Optica.crearOrden({
          pacienteId: luis.id, estado: 'LISTA', fechaPromesa: hoy(),
          armazonMarca: 'Oakley', micaMaterial: 'ORGANICO', micaDiseno: 'MONOFOCAL',
          gOdEsfera: -0.75, gOdCilindro: 0, gOdEje: 0,
          gOiEsfera: -0.50, gOiCilindro: 0, gOiEje: 0,
          costoArmazon: 720, costoMicas: 280, total: 1000, anticipo: 400,
        }).catch(() => {});
      }
      details.push('Órdenes de taller');
    }

    await seedCitasGeneric(CITAS_OPTICA, NOMBRES_CITAS_OPTICA, details, 'Citas óptica hoy');

    await seedExpedienteRubro('clinica-optica', details);

    return { ok: true, message: 'Demo óptica sembrada.', details };
  } catch (e: any) {
    return { ok: false, message: e?.message ?? String(e), details };
  }
}

// ── Farmacia clínica ─────────────────────────────────────────────────────────

export async function seedFarmaciaClinicaDemo(): Promise<SeedResult> {
  const details: string[] = [];
  try {
    const prods = await FarmaciaClinica.productos().catch(() => []);
    const existentes = new Set(prods.map(p => p.codigo?.toUpperCase()));
    let insertados = 0;

    for (const code of MEDICAMENTOS_ALTA_ROTACION) {
      const m = getMedicamentoByCodigo(code);
      if (!m || existentes.has(code)) continue;
      const seed = medicamentoToProductoSeed(m, 30 + (insertados % 5) * 10);
      await FarmaciaClinica.crearProducto(seed);
      existentes.add(code);
      insertados++;
    }

    if (insertados === 0 && prods.length >= 10) {
      return {
        ok: true,
        message: 'Farmacia clínica ya tiene catálogo MSPAS.',
        details: [`${prods.length} ítems · catálogo nacional`],
      };
    }

    details.push(`${insertados || prods.length} medicamentos MSPAS (Guatemala)`);
    return { ok: true, message: 'Catálogo farmacia clínica sembrado.', details };
  } catch (e: any) {
    return { ok: false, message: e?.message ?? String(e), details };
  }
}

async function seedInsumosRubro(org: ClinicPreviewOrg, details: string[]): Promise<void> {
  const prods = await FarmaciaClinica.productos().catch(() => []);
  const codes = new Set(prods.map(p => p.codigo?.toUpperCase()));

  const insumos: Array<{ codigo: string; nombre: string; categoria: 'INSUMO' | 'PRODUCTO'; precio: number; stock: number }> =
    org === 'clinica-estetica'
      ? [
          { codigo: 'EST-BOTOX', nombre: 'Toxina botulínica (unidad)', categoria: 'INSUMO', precio: 185, stock: 12 },
          { codigo: 'EST-HIAL', nombre: 'Ácido hialurónico 1 ml', categoria: 'INSUMO', precio: 420, stock: 8 },
          { codigo: 'EST-AGUJA', nombre: 'Aguja 30G estéril', categoria: 'INSUMO', precio: 3.5, stock: 100 },
        ]
      : org === 'clinica-odontologica'
        ? [
            { codigo: 'ODO-Composite', nombre: 'Composite A2', categoria: 'INSUMO', precio: 85, stock: 20 },
            { codigo: 'ODO-Anest', nombre: 'Anestesia local carpule', categoria: 'INSUMO', precio: 12, stock: 50 },
          ]
        : org === 'clinica-nutricion'
          ? [
              { codigo: 'NUT-Prot', nombre: 'Suplemento proteína 454 g', categoria: 'PRODUCTO', precio: 195, stock: 15 },
              { codigo: 'NUT-Fibra', nombre: 'Fibra soluble sobre', categoria: 'PRODUCTO', precio: 8, stock: 60 },
            ]
          : [];

  let n = 0;
  for (const i of insumos) {
    if (codes.has(i.codigo.toUpperCase())) continue;
    await FarmaciaClinica.crearProducto({
      ...i, unidad: 'UNIDAD', requiereReceta: false, afectaInventario: true, activo: true,
    }).catch(() => {});
    n++;
  }
  if (n > 0) details.push(`${n} insumos ${org.replace('clinica-', '')}`);
}

/** Importa un medicamento del catálogo nacional al inventario POS de la clínica. */
export async function importMedicamentoToFarmacia(codigo: string): Promise<SeedResult> {
  const m = getMedicamentoByCodigo(codigo);
  if (!m) return { ok: false, message: `Código ${codigo} no encontrado en catálogo MSPAS.` };
  try {
    const prods = await FarmaciaClinica.productos().catch(() => []);
    if (prods.some(p => p.codigo?.toUpperCase() === m.codigo.toUpperCase())) {
      return { ok: true, message: `${m.codigo} ya está en inventario.` };
    }
    await FarmaciaClinica.crearProducto(medicamentoToProductoSeed(m));
    return { ok: true, message: `${m.codigo} · ${m.nombre} agregado.` };
  } catch (e: any) {
    return { ok: false, message: e?.message ?? String(e) };
  }
}

export { CATALOGO_MEDICAMENTOS_MSPAS };

// ── Pacientes + expediente humano ─────────────────────────────────────────────

async function asegurarPacienteCui(def: DemoPatientDef): Promise<Paciente> {
  try {
    return await Pacientes.porCui(def.cui);
  } catch {
    return Pacientes.crear({
      nombres: def.nombres,
      apellidos: def.apellidos,
      cui: def.cui,
      sexo: def.sexo,
      telefono: def.telefono,
      fechaNacimiento: def.fechaNacimiento,
    });
  }
}

function nombrePaciente(p: Paciente): string {
  return `${p.nombres} ${p.apellidos}`.trim();
}

async function asegurarRutinaFisio(
  pacienteId: string,
  rutina: RutinaFisio,
  details: string[],
): Promise<void> {
  const existentes = await Notas.porPaciente(pacienteId).catch(() => []);
  if (existentes.some(n => n.tipo === RUTINA_FISIO_TIPO && n.contenido.includes(rutina.titulo.slice(0, 24)))) return;
  const ok = await tryStep('Rutina domiciliaria', async () => {
    await Notas.crear({
      pacienteId,
      autor: FISIO_PROFESIONAL_DEMO,
      tipo: RUTINA_FISIO_TIPO,
      contenido: serializeRutina(rutina),
    });
  }, details);
  if (ok) details.push(`Rutina: ${rutina.titulo}`);
}

async function seedRutinasFisio(pacientes: Paciente[], details: string[]): Promise<void> {
  if (pacientes[0]?.id) await asegurarRutinaFisio(pacientes[0].id, RUTINA_DEMO_LUMBAR, details);
  if (pacientes[1]?.id) await asegurarRutinaFisio(pacientes[1].id, RUTINA_DEMO_HOMBRO, details);
}

async function asegurarNotasExpediente(
  pacienteId: string,
  notas: DemoNota[],
  details: string[],
): Promise<number> {
  const existentes = await Notas.porPaciente(pacienteId).catch(() => []);
  const prefijos = new Set(existentes.map(n => (n.contenido ?? '').slice(0, 48)));
  let added = 0;
  for (const n of notas) {
    if (prefijos.has(n.contenido.slice(0, 48))) continue;
    const ok = await tryStep('Nota clínica', async () => {
      await Notas.crear({ pacienteId, autor: n.autor, contenido: n.contenido });
    }, details);
    if (ok) {
      added++;
      prefijos.add(n.contenido.slice(0, 48));
    }
  }
  return added;
}

async function asegurarSignosExpediente(
  pacienteId: string,
  signosDef: Omit<Signo, 'id' | 'registradoEn'>[],
  details: string[],
): Promise<number> {
  const existentes = await Signos.porPaciente(pacienteId).catch(() => []);
  if (existentes.length >= signosDef.length) return 0;
  let added = 0;
  for (const s of signosDef) {
    if (existentes.length + added >= signosDef.length) break;
    const ok = await tryStep('Signos vitales', async () => {
      await Signos.registrar({ ...s, pacienteId });
    }, details);
    if (ok) added++;
  }
  return added;
}

async function seedExpedienteRubro(org: ClinicPreviewOrg, details: string[]): Promise<Paciente[]> {
  const defs = RUBRO_PACIENTES[org] ?? RUBRO_PACIENTES['clinica-medica']!;
  const out: Paciente[] = [];
  let totalNotas = 0;

  for (const def of defs) {
    const p = await asegurarPacienteCui(def);
    if (!p.id) continue;
    out.push(p);
    totalNotas += await asegurarNotasExpediente(p.id, def.notas, details);
  }

  if (totalNotas > 0) {
    details.push(`${totalNotas} notas clínicas en expediente`);
  }

  if (org !== 'clinica-psicologia') {
    const signosNutricion: Omit<Signo, 'id' | 'registradoEn'>[] = [
      { pacienteId: '', peso: 78.5, talla: 1.65, ta: '118/76', fc: 68 },
      { pacienteId: '', peso: 77.2, talla: 1.65, ta: '116/74', fc: 70 },
    ];
    const signosFisio: Omit<Signo, 'id' | 'registradoEn'>[] = [
      { pacienteId: '', ta: '7/10', fc: 40, fr: 15, temp: 4, satO2: 68, peso: 92, talla: null },
      { pacienteId: '', ta: '4/10', fc: 55, fr: 18, temp: 4, satO2: 82, peso: 90, talla: null },
    ];
    const signosGeneral: Omit<Signo, 'id' | 'registradoEn'>[] = [
      { pacienteId: '', ta: '120/78', fc: 72, fr: 18, temp: 36.5, satO2: 98, peso: 70, talla: 1.68 },
      { pacienteId: '', ta: '118/76', fc: 70, fr: 17, temp: 36.4, satO2: 99, peso: 69.5, talla: 1.68 },
    ];
    const plantilla =
      org === 'clinica-nutricion' ? signosNutricion :
      org === 'clinica-fisioterapia' ? signosFisio :
      signosGeneral;
    let signosAdded = 0;
    for (const p of out.slice(0, 2)) {
      if (!p.id) continue;
      signosAdded += await asegurarSignosExpediente(p.id, plantilla, details);
    }
    if (signosAdded > 0) {
      details.push(
        org === 'clinica-nutricion' ? 'Antropometría en expediente' :
        org === 'clinica-fisioterapia' ? 'Evaluación funcional en expediente' :
        'Signos vitales en expediente',
      );
    }
  }

  if (org === 'clinica-odontologica') {
    const pDental = out.find(p => p.cui === DEMO_CUIS.dental) ?? out[0];
    if (pDental) await seedPlacasOdonto(pDental, details);
  }

  if (org === 'clinica-fisioterapia') {
    await seedRutinasFisio(out, details);
  }

  details.push(`Expediente demo · CUI ${demoCuiPrincipal(org)}`);
  return out;
}

async function seedPlacasOdonto(pDental: Paciente, details: string[]): Promise<void> {
  const placas: PlacaDental[] = [
    {
      id: 'demo-rx-16',
      pacienteId: pDental.id!,
      cui: DEMO_CUIS.dental,
      fecha: hoy(),
      tipo: 'PERIAPICAL',
      dientes: '16',
      nota: 'Lesión periapical incipiente',
      medico: 'Dra. Odonto Demo',
      registradaEn: new Date().toISOString(),
    },
    {
      id: 'demo-rx-pano',
      pacienteId: pDental.id!,
      cui: DEMO_CUIS.dental,
      fecha: hoy(),
      tipo: 'PANORAMICA',
      dientes: 'Arcada completa',
      nota: 'Panorámica inicial de referencia',
      medico: 'Dra. Odonto Demo',
      registradaEn: new Date().toISOString(),
    },
  ];
  seedPlacasDemo(placas);
  details.push('2 placas dentales demo');
}

async function seedCitasGeneric(
  defs: CitaDemoDef[],
  nombres: string[],
  details: string[],
  label: string,
  pacientes?: Paciente[],
): Promise<void> {
  const citasHoy = await Citas.listar(hoy()).catch(() => []);
  if (citasHoy.length >= defs.length) return;

  for (let i = 0; i < defs.length; i++) {
    const d = defs[i];
    const idx = d.pacienteIdx ?? i;
    const pac = pacientes?.[idx];
    const nombre = pac ? nombrePaciente(pac) : (nombres[i] ?? nombres[0] ?? 'Paciente demo');
    const yaExiste = citasHoy.some(c =>
      c.motivo === d.motivo && (c.pacienteNombre === nombre || c.pacienteId === pac?.id),
    );
    if (yaExiste) continue;

    await Citas.crear({
      pacienteId: pac?.id,
      pacienteNombre: nombre,
      telefono: pac?.telefono ?? undefined,
      profesional: d.profesional,
      consultorio: d.consultorio,
      motivo: d.motivo,
      fechaHora: horaDemo(d.hour, d.min ?? 0),
      duracionMin: 30,
      estado: d.estado ?? 'PROGRAMADA',
    }).catch(() => {});
  }
  details.push(label);
}

async function seedCitasRubro(org: ClinicPreviewOrg, pacientes: Paciente[], details: string[]): Promise<void> {
  const defs = CITAS_RUBRO[org];
  if (!defs?.length) return;
  await seedCitasGeneric(defs, pacientes.map(nombrePaciente), details, 'Citas hoy', pacientes);
}

async function seedSignosRubro(_org: ClinicPreviewOrg, _pacientes: Paciente[], _details: string[]): Promise<void> {
  /* Signos se siembran en seedExpedienteRubro */
}

async function seedRecetaFarmacia(org: ClinicPreviewOrg, pacientes: Paciente[], details: string[]): Promise<void> {
  const withFarmacia = org === 'clinica-medica' || org === 'clinica-odontologica' || org === 'clinica-estetica';
  if (!withFarmacia || !pacientes[0]?.id) return;

  const recetas = await FarmaciaClinica.recetas().catch(() => []);
  if (recetas.length >= 1) return;

  const diag =
    org === 'clinica-odontologica' ? 'Profilaxis dental' :
    org === 'clinica-estetica' ? 'Post procedimiento estético' : 'Control ambulatorio';

  await FarmaciaClinica.crearReceta({
    pacienteId: pacientes[0].id,
    medicoId: (await Consultorios.profesionales(true).catch(() => []))[0]?.id ?? null,
    diagnostico: diag,
    items: [
      { descripcion: 'Amoxicilina 500 mg', cantidad: 21, indicaciones: '1 cápsula c/8h por 7 días' },
      { descripcion: 'Paracetamol 500 mg', cantidad: 10, indicaciones: '1 tableta c/6h si fiebre' },
    ],
  }).catch(() => {});
  details.push('Receta mostrador');
}

async function seedDespachoPendienteDemo(pacienteId: string | undefined, details: string[]): Promise<void> {
  if (!pacienteId) return;
  const ventas = await FarmaciaClinica.ventas(pacienteId).catch(() => []);
  if (ventas.some(v => v.estado === 'PENDIENTE_COBRO')) return;

  const prods = await FarmaciaClinica.productos().catch(() => []);
  const p = prods.find(x => (x.stock ?? 0) > 0 && (x.precio ?? 0) > 0);
  if (!p?.id) return;

  await FarmaciaClinica.despachar({
    pacienteId,
    formaPago: 'EFECTIVO',
    quiereFactura: false,
    notas: 'Demo · despacho pendiente de cobro',
    items: [{ productoId: p.id, cantidad: 1, precioUnitario: p.precio }],
  }).catch(() => {});
  details.push('Despacho farmacia pendiente de cobro');
}

async function seedCobroPostergadoDemo(pacienteId: string | undefined, details: string[]): Promise<void> {
  if (!pacienteId) return;
  const bandeja = await Cobros.pendientes().catch(() => []);
  if (bandeja.some(c => c.pacienteId === pacienteId && c.estado === 'PENDIENTE')) return;

  const resumen = await Cobros.resumen(pacienteId).catch(() => null);
  if (!resumen || Number(resumen.montoTotalPendiente) <= 0) return;

  await Cobros.cerrar({
    pacienteId,
    tipoDocumento: 'POSTERGAR',
    cuentaId: resumen.cuentasAbiertas[0]?.id,
    ventaIds: resumen.ventasPendientes.map(v => v.id!).filter(Boolean),
    notas: 'Demo · recordar más tarde',
  }).catch(() => {});
  details.push('Cobro postergado en bandeja');
}

async function seedCuentaPacienteDemo(pacienteId: string, details: string[]): Promise<void> {
  const cuentas = await Cuentas.list().catch(() => []);
  if (cuentas.some(c => c.pacienteId === pacienteId && c.estado === 'ABIERTA')) return;

  const c = await Cuentas.abrir({ pacienteId, aseguradora: null }).catch(() => null);
  if (!c?.id) return;

  await Cuentas.renglon(c.id, {
    descripcion: 'Consulta ambulatoria',
    cantidad: 1,
    precioUnitario: 350,
  }).catch(() => {});
  details.push('Cuenta paciente (cobros secretaría)');
}

async function seedAmbulatorioClinica(org: ClinicPreviewOrg, details: string[]): Promise<Paciente[]> {
  const pacientes = await seedExpedienteRubro(org, details);
  if (org === 'clinica-medica') {
    await tryStep('Catálogo LIS', async () => {
      const r = await Laboratorio.seed();
      return `${r.total} exámenes`;
    }, details);
    await tryStep('Catálogo RIS', async () => {
      const r = await Imagenologia.seed();
      return `${r.total} estudios`;
    }, details);

    const lab = await Laboratorio.ordenes().catch(() => []);
    if (lab.length < 1 && pacientes[0]?.id) {
      await Laboratorio.crearOrden({
        pacienteId: pacientes[0].id,
        pacienteNombre: nombrePaciente(pacientes[0]),
        medicoSolicitante: 'Dr. Clínica Demo',
        examenes: ['HEM-001', 'QUI-GLU'],
        prioridad: 'RUTINA',
      }).catch(() => {});
      details.push('Orden laboratorio');
    }
  }

  await seedCitasRubro(org, pacientes, details);
  await seedSignosRubro(org, pacientes, details);

  const withFarmacia = org === 'clinica-medica' || org === 'clinica-odontologica' || org === 'clinica-estetica';
  if (withFarmacia) {
    await seedInsumosRubro(org, details);
    await seedRecetaFarmacia(org, pacientes, details);
    await seedDespachoPendienteDemo(pacientes[0]?.id, details);
  }

  if (pacientes[0]?.id) {
    await seedCuentaPacienteDemo(pacientes[0].id, details);
  }
  if (pacientes[1]?.id) {
    await seedCuentaPacienteDemo(pacientes[1].id, details);
    await seedCobroPostergadoDemo(pacientes[1].id, details);
  } else if (pacientes[0]?.id) {
    await seedCobroPostergadoDemo(pacientes[0].id, details);
  }

  return pacientes;
}

export async function seedClinicaMedicaDemo(org?: ClinicPreviewOrg | string | null): Promise<SeedResult> {
  const details: string[] = [];
  const rubro = (org ?? 'clinica-medica') as ClinicPreviewOrg;
  try {
    const withFarmacia =
      rubro === 'clinica-medica' || rubro === 'clinica-odontologica' || rubro === 'clinica-estetica';
    if (withFarmacia) {
      const farm = await seedFarmaciaClinicaDemo();
      if (farm.details) details.push(...farm.details);
    } else if (rubro === 'clinica-nutricion') {
      await seedInsumosRubro(rubro, details);
    }

    await seedAmbulatorioClinica(rubro, details);

    return { ok: true, message: `Demo ${rubro.replace('clinica-', '')} sembrada.`, details };
  } catch (e: any) {
    return { ok: false, message: e?.message ?? String(e), details };
  }
}

export async function seedClinicByOrg(org: ClinicPreviewOrg | string | null): Promise<SeedResult> {
  let result: SeedResult;
  switch (org) {
    case 'clinica-veterinaria': {
      result = await seedVeterinariaDemo();
      await seedFarmaciaClinicaDemo().catch(() => null);
      break;
    }
    case 'clinica-optica': {
      result = await seedOpticaDemo();
      await seedFarmaciaClinicaDemo().catch(() => null);
      break;
    }
    case 'clinica-medica':
    case 'clinica-odontologica':
    case 'clinica-nutricion':
    case 'clinica-psicologia':
    case 'clinica-estetica':
    case 'clinica-fisioterapia':
      result = await seedClinicaMedicaDemo(org);
      break;
    default:
      result = await seedClinicaMedicaDemo(org);
  }
  if (org && org !== 'clinica-veterinaria') {
    pinExpedienteDemoCui(org);
  }
  return result;
}

/** Punto único: hospital o clínica según contexto. */
export async function seedDemoData(opts: {
  org?: ClinicPreviewOrg | string | null;
  mode: 'hospital' | 'clinic';
}): Promise<SeedResult> {
  if (opts.mode === 'hospital') return seedHospitalDemo();
  return seedClinicByOrg(opts.org ?? 'clinica-medica');
}
